// The cast's acts (4 Nov 2026): short choreographies, about 1.5 to 3 s, built from keys over time. Each key is a state
// the character reaches at time t (ms from the act's start), on up to six tracks:
//
//   w      the wrapper's transform, in units of the character's size (x, y; y up is negative), r degrees, s / sx / sy
//          scale, o opacity. A key's w is the whole state: what it leaves out is at rest.
//   a      the arms, [left, right] degrees about the shoulders (0 hangs; left +ve and right -ve swing out and up)
//   p      the face's pose (rig.ts POSES), reached at t, blended in over `blend` ms (220 by default). A pose key
//          without `a` also brings the pose's own arms.
//   front  which arm is drawn in front of the face (a hand to the chin, a facepalm); steps, never blends
//   hand   a friend's hand coming in from the right for a high five, 0 away .. 1 there
//   burst  a burst of sparks, 0..1 through its life (hidden at 0 and at 1), at the act's burstAt
//
// `e` eases the move that arrives at the key. The last key carries the act's end pose; the wrapper may end away from
// rest (a deflate stays small), and the mood's loop plays on top of where it ends. Transform and opacity only.
import { POSES, faceOf, mixFace, type Face, type PoseName } from './rig';
import type { CastMood } from './CastMascot';

export interface WT { x: number; y: number; r: number; s: number; sx: number; sy: number; o: number }
export const REST: Readonly<WT> = { x: 0, y: 0, r: 0, s: 1, sx: 1, sy: 1, o: 1 };

export type Ease = 'lin' | 'in' | 'out' | 'io' | 'back';
export type Arms = readonly [number, number];

export interface Key {
  t: number;
  w?: Partial<WT>;
  a?: Arms;
  p?: PoseName;
  blend?: number;
  e?: Ease;
  front?: readonly [0 | 1, 0 | 1];
  hand?: number;
  burst?: number;
}

export interface Act {
  name: string;
  label: string;
  moods: readonly CastMood[];
  ms: number;
  keys: readonly Key[];
  /** where the burst goes, as fractions of the box */
  burstAt?: readonly [number, number];
}

/** The fields a key may carry (acts.test.ts holds every key to these). */
export const KEY_FIELDS = ['t', 'w', 'a', 'p', 'blend', 'e', 'front', 'hand', 'burst'] as const;
export const WT_FIELDS = ['x', 'y', 'r', 's', 'sx', 'sy', 'o'] as const;

const k = (t: number, o: Omit<Key, 't'> = {}): Key => ({ t, ...o });

// ---------- builders for the repetitive bits ----------

/** Wings: arms beating between up and out, every `step` ms from `from` to `to`. */
const flap = (from: number, to: number, step = 140): Key[] => {
  const out: Key[] = [];
  for (let t = from, i = 0; t <= to; t += step, i++) out.push(k(t, { a: i % 2 ? [80, -80] : [145, -145], e: 'io' }));
  return out;
};

/** Side-steps: hips swing, the leading arm goes up, a small hop between steps. */
const steps = (from: number, n: number, beat: number, big: number, face?: (i: number) => PoseName | undefined): Key[] => {
  const out: Key[] = [];
  for (let i = 0; i < n; i++) {
    const t = from + i * beat; const left = i % 2 === 0;
    out.push(k(t, { w: { x: (left ? -0.09 : 0.09) * big, r: (left ? -9 : 9) * big, sy: 0.94, sx: 1.04 }, a: left ? [140, -40] : [40, -140], e: 'out', p: face?.(i) }));
    out.push(k(t + beat / 2, { w: { y: -0.07 * big, sy: 1.05, sx: 0.97 }, e: 'io' }));
  }
  return out;
};

/** A fall from above the screen into the box, a squash on landing and a bounce. */
const dropKeys = (land: PoseName): Key[] => [
  k(0, { w: { y: -3.2, o: 0, sy: 1.12, sx: 0.9 }, p: 'surprised' }),
  k(90, { w: { y: -2.9, sy: 1.12, sx: 0.9 }, e: 'lin' }),
  k(430, { w: { y: 0, sy: 1.14, sx: 0.9 }, e: 'in' }),
  k(540, { w: { sy: 0.74, sx: 1.22 }, e: 'out', p: 'happy', blend: 120 }),
  k(700, { w: { y: -0.28, sy: 1.08, sx: 0.95 }, e: 'out' }),
  k(860, { w: { y: 0, sy: 0.9, sx: 1.07 }, e: 'in' }),
  k(1000, { w: { y: -0.06, sy: 1.02 }, e: 'out' }),
  k(1120, { w: {}, e: 'in' }),
  k(1600, { p: land }),
];

/** Small tremors on x, every `every` ms. */
const jitter = (from: number, to: number, every: number, amp: number, base: Partial<WT> = {}): Key[] => {
  const out: Key[] = [];
  for (let t = from, i = 0; t <= to; t += every, i++) out.push(k(t, { w: { ...base, x: (base.x ?? 0) + (i % 2 ? amp : -amp) }, e: 'io' }));
  return out;
};

/** Sobs: the shoulders heave. */
const sobs = (from: number, to: number): Key[] => {
  const out: Key[] = [];
  for (let t = from, i = 0; t <= to; t += 170, i++) out.push(k(t, { w: i % 2 ? { sy: 1.01 } : { sy: 0.94, sx: 1.04, y: 0.015 }, e: 'io' }));
  return out;
};

/** A loop-the-loop: up, round to the right, over the top and back down, the body turning with it. */
const loopPath = (from: number, to: number, n = 12): Key[] => {
  const out: Key[] = [];
  for (let i = 0; i <= n; i++) {
    const th = (i / n) * Math.PI * 2;
    out.push(k(from + ((to - from) * i) / n, { w: { x: 0.55 * Math.sin(th), y: -1.05 + 0.5 * Math.cos(th), r: -(i / n) * 360 }, e: 'lin' }));
  }
  return out;
};

// ---------- the catalogue ----------

const sorted = (keys: Key[]): Key[] => [...keys].sort((a, b) => a.t - b.t);
const act = (a: Omit<Act, 'keys'> & { keys: Key[] }): Act => ({ ...a, keys: sorted(a.keys) });

export const ACTS = {
  // right
  dance: act({ name: 'dance', label: 'Dance', moods: ['right'], ms: 2400, keys: [
    k(0, { p: 'happy' }), ...steps(150, 7, 300, 1, (i) => (i === 3 ? 'joy' : i === 5 ? 'happy' : undefined)), k(2400, { w: {}, p: 'joy' }),
  ] }),
  'high-five': act({ name: 'high-five', label: 'High five', moods: ['right'], ms: 2200, burstAt: [0.98, 0.16], keys: [
    k(0, { p: 'happy', hand: 0, burst: 0 }),
    k(250, { w: { sy: 0.9, sx: 1.06 }, a: [18, 10], e: 'out' }),
    k(600, { hand: 0.65, e: 'out' }),
    k(740, { w: { x: 0.1, y: -0.1, r: 10, sy: 1.06, sx: 0.96 }, a: [40, -140], hand: 1, burst: 0, e: 'out', p: 'joy', blend: 120 }),
    k(900, { w: { x: 0.04, sx: 1.06, sy: 0.94 }, a: [30, -140], e: 'out' }),
    k(960, { hand: 1 }),
    k(1180, { hand: 0, e: 'in' }),
    k(1300, { burst: 1, e: 'out' }),
    k(1350, { w: { y: -0.14, sy: 1.05 }, e: 'out' }),
    k(1550, { w: { sy: 0.92, sx: 1.05 }, e: 'in' }),
    k(1750, { w: {}, e: 'out' }),
    k(2200, { p: 'happy' }),
  ] }),
  fly: act({ name: 'fly', label: 'Fly', moods: ['right'], ms: 2800, keys: [
    k(0, { p: 'happy' }),
    k(250, { w: { sy: 0.86, sx: 1.1 }, a: [60, -60], e: 'out', p: 'determined' }),
    k(450, { w: { y: -0.55, sy: 1.1, sx: 0.92 }, e: 'out', p: 'joy', blend: 140 }),
    ...flap(450, 2100),
    ...loopPath(560, 1900),
    k(2150, { w: { y: -0.1, r: -360, sy: 1.06 }, a: [120, -120], e: 'io' }),
    k(2300, { w: { y: 0, r: -360, sy: 0.84, sx: 1.12 }, e: 'in' }),
    k(2480, { w: { r: -360, sy: 1.05, sx: 0.97 }, e: 'out' }),
    k(2650, { w: { r: -360 }, e: 'io' }),
    k(2800, { p: 'happy' }),
  ] }),
  'wink-point': act({ name: 'wink-point', label: 'Wink and point', moods: ['right'], ms: 1800, burstAt: [0.34, 0.36], keys: [
    k(0, { p: 'neutral', burst: 0 }),
    k(250, { w: { r: -5, x: -0.03 }, p: 'happy', e: 'io' }),
    k(520, { w: { r: 6, x: 0.03, sx: 1.03 }, p: 'wink', e: 'back', burst: 0 }),
    k(650, { a: [18, -108] }), k(780, { a: [18, -138] }), k(900, { a: [18, -118], burst: 1 }),
    k(1200, { w: { r: 5 }, a: [18, -128] }),
    k(1800, { w: {}, p: 'happy', e: 'io' }),
  ] }),
  'jump-spin': act({ name: 'jump-spin', label: 'Jump and spin', moods: ['right'], ms: 1900, burstAt: [0.5, 0.3], keys: [
    k(0, { p: 'happy', burst: 0 }),
    k(220, { w: { sy: 0.84, sx: 1.12 }, a: [40, -40], e: 'out', p: 'determined' }),
    k(420, { w: { y: -0.7, sy: 1.12, sx: 0.9 }, a: [140, -140], e: 'out', p: 'joy', blend: 140, burst: 0 }),
    k(560, { w: { y: -0.85, sx: -0.9 }, e: 'io' }),
    k(700, { w: { y: -0.9, sx: 0.9 }, e: 'io' }),
    k(840, { w: { y: -0.85, sx: -0.9 }, e: 'io' }),
    k(980, { w: { y: -0.7 }, e: 'io', burst: 1 }),
    k(1180, { w: { y: 0, sy: 1.08, sx: 0.94 }, e: 'in' }),
    k(1280, { w: { sy: 0.84, sx: 1.14 }, e: 'out' }),
    k(1450, { w: { y: -0.08, sy: 1.04 }, e: 'out' }),
    k(1600, { w: {}, e: 'in' }),
    k(1900, { p: 'joy' }),
  ] }),
  backflip: act({ name: 'backflip', label: 'Backflip', moods: ['right'], ms: 1700, burstAt: [0.5, 0.35], keys: [
    k(0, { p: 'happy', burst: 0 }),
    k(250, { w: { sy: 0.82, sx: 1.14 }, a: [30, -30], e: 'out', p: 'determined' }),
    k(450, { w: { y: -0.6, sy: 1.1, sx: 0.92, r: -70 }, a: [140, -140], e: 'out', p: 'joy', blend: 140 }),
    k(700, { w: { y: -0.95, r: -200 }, a: [120, -120], e: 'lin', burst: 0 }),
    k(950, { w: { y: -0.6, r: -330 }, a: [140, -140], e: 'lin' }),
    k(1130, { w: { y: 0, r: -360, sy: 1.06 }, e: 'in', burst: 1 }),
    k(1230, { w: { r: -360, sy: 0.82, sx: 1.14 }, e: 'out' }),
    k(1420, { w: { r: -360, sy: 1.04, sx: 0.98 }, e: 'out' }),
    k(1560, { w: { r: -360 }, e: 'io' }),
    k(1700, { p: 'joy' }),
  ] }),
  'drop-bounce': act({ name: 'drop-bounce', label: 'Drop in', moods: ['right'], ms: 1600, keys: dropKeys('joy') }),

  // wrong
  splat: act({ name: 'splat', label: 'Fall and splat', moods: ['wrong'], ms: 2400, keys: [
    k(0, { w: { y: -3.2, o: 0, sy: 1.1, sx: 0.92 }, p: 'surprised' }),
    k(80, { w: { y: -3, sy: 1.1, sx: 0.92 }, e: 'lin' }),
    k(420, { w: { y: 0, sy: 1.16, sx: 0.88 }, e: 'in' }),
    k(470, { w: { sy: 0.32, sx: 1.5 }, e: 'out', p: 'dizzy', blend: 80 }),
    k(560, { w: { sy: 0.4, sx: 1.42 }, e: 'out' }),
    k(1150, { w: { sy: 0.36, sx: 1.46 }, e: 'io' }),
    k(1330, { w: { y: -0.16, sy: 1.14, sx: 0.9 }, e: 'out', p: 'sad', blend: 160 }),
    k(1520, { w: { y: 0, sy: 0.93, sx: 1.05 }, e: 'in' }),
    k(1700, { w: {}, e: 'out' }),
    k(2400, { p: 'sad' }),
  ] }),
  faint: act({ name: 'faint', label: 'Faint', moods: ['wrong'], ms: 2600, keys: [
    k(0, { p: 'surprised' }),
    k(250, { w: { y: -0.06 }, e: 'out' }),
    k(420, { w: {}, p: 'dizzy', e: 'in' }),
    k(650, { w: { r: -7 }, e: 'io' }),
    k(850, { w: { r: 6 }, e: 'io' }),
    k(1050, { w: { r: -4 }, e: 'io' }),
    k(1450, { w: { r: 88, x: 0.32, y: 0.22 }, e: 'in' }),
    k(1560, { w: { r: 80, x: 0.3, y: 0.17 }, e: 'out' }),
    k(1660, { w: { r: 88, x: 0.32, y: 0.22 }, e: 'in' }),
    k(2050, { w: { r: 88, x: 0.32, y: 0.22 } }),
    k(2350, { w: {}, e: 'back', p: 'sad' }),
    k(2600, { p: 'sad' }),
  ] }),
  cry: act({ name: 'cry', label: 'Cry', moods: ['wrong'], ms: 2600, keys: [
    k(0, { p: 'surprised' }),
    k(300, { p: 'sad' }),
    k(650, { p: 'crying' }),
    ...sobs(700, 2100),
    k(2600, { w: {}, p: 'sad', blend: 400 }),
  ] }),
  facepalm: act({ name: 'facepalm', label: 'Facepalm', moods: ['wrong'], ms: 2200, keys: [
    k(0, { p: 'surprised' }),
    k(300, { p: 'sad', a: [4, -4] }),
    k(420, { a: [4, 20], front: [0, 1] }),
    k(700, { a: [4, 98], w: { r: 7, y: 0.03 }, e: 'out' }),
    k(900, { w: { r: 3, y: 0.03 }, e: 'io' }), k(1100, { w: { r: 8, y: 0.03 }, e: 'io' }), k(1300, { w: { r: 4, y: 0.03 }, e: 'io' }),
    k(1600, { a: [4, 98], w: { r: 6, y: 0.03 }, e: 'io' }),
    k(1850, { a: [4, 14], front: [0, 0], e: 'io' }),
    k(2200, { w: {}, p: 'sad', e: 'io' }),
  ] }),
  deflate: act({ name: 'deflate', label: 'Deflate', moods: ['wrong'], ms: 2000, keys: [
    k(0, { w: { s: 1.06 }, p: 'surprised' }),
    k(250, { w: { s: 1.08 }, p: 'sad', e: 'out' }),
    ...[0, 1, 2, 3, 4, 5].map((i) => k(400 + i * 170, { w: { s: 1.08 - 0.05 * (i + 1), x: i % 2 ? 0.02 : -0.02, r: i % 2 ? 3 : -3 }, e: 'io' })),
    k(1500, { w: { s: 0.76, sy: 0.9, sx: 1.06 }, e: 'out' }),
    k(2000, { w: { s: 0.8, sy: 0.92, sx: 1.04 }, p: 'sad', e: 'io' }),
  ] }),
  shiver: act({ name: 'shiver', label: 'Shiver', moods: ['wrong'], ms: 1800, keys: [
    k(0, { p: 'nervous' }),
    ...jitter(100, 1450, 50, 0.025, { sy: 0.97 }),
    k(1800, { w: {}, p: 'sad', e: 'io' }),
  ] }),

  // partial
  shrug: act({ name: 'shrug', label: 'Shrug', moods: ['partial'], ms: 1800, keys: [
    k(0, { p: 'neutral' }),
    k(300, { w: { y: -0.05, sy: 1.05, sx: 0.97 }, a: [105, -105], p: 'confused', e: 'out' }),
    k(650, { w: { y: -0.05, sy: 1.05, sx: 0.97 }, a: [100, -100] }),
    k(850, { w: {}, a: [60, -60], e: 'io' }),
    k(1050, { w: { y: -0.04, sy: 1.04 }, a: [105, -105], e: 'out' }),
    k(1350, { w: {}, a: [40, -18], e: 'io' }),
    k(1800, { p: 'confused' }),
  ] }),
  think: act({ name: 'think', label: 'Think', moods: ['partial'], ms: 2200, keys: [
    k(0, { p: 'neutral' }),
    k(300, { p: 'think', a: [18, 20], front: [0, 1] }),
    k(600, { a: [18, 68], w: { r: -4 }, e: 'out' }),
    k(800, { a: [18, 62] }), k(1000, { a: [18, 70] }), k(1200, { a: [18, 62] }), k(1400, { a: [18, 68] }),
    k(1600, { p: 'confused', a: [18, 64], w: { r: -6 } }),
    k(1900, { a: [18, 14], front: [0, 0], w: {}, e: 'io' }),
    k(2200, { p: 'confused' }),
  ] }),
  wobble: act({ name: 'wobble', label: 'Wobble', moods: ['partial'], ms: 1600, keys: [
    k(0, { p: 'surprised' }),
    k(160, { w: { r: -10, sx: 1.06, sy: 0.94 }, e: 'out' }),
    k(360, { w: { r: 8, sx: 0.95, sy: 1.05 }, e: 'io' }),
    k(560, { w: { r: -6, sx: 1.04, sy: 0.96 }, e: 'io' }),
    k(760, { w: { r: 4, sx: 0.97, sy: 1.03 }, e: 'io', p: 'confused' }),
    k(960, { w: { r: -2 }, e: 'io' }),
    k(1150, { w: {}, e: 'io' }),
    k(1600, { p: 'surprised' }),
  ] }),

  // none (no answer)
  'fall-asleep': act({ name: 'fall-asleep', label: 'Fall asleep', moods: ['none'], ms: 2800, keys: [
    k(0, { p: 'neutral' }),
    k(700, { p: 'sleepy', blend: 600 }),
    k(900, { w: { r: 6, y: 0.02 }, e: 'in' }),
    k(1100, { w: { r: 1 }, e: 'out' }),
    k(1650, { w: { r: 10, y: 0.03 }, e: 'in' }),
    k(1850, { w: { r: 3, y: 0.01 }, e: 'out' }),
    k(2450, { w: { r: 12, y: 0.04 }, e: 'in' }),
    k(2800, { w: { r: 7, y: 0.03 }, e: 'io', p: 'sleepy' }),
  ] }),
  yawn: act({ name: 'yawn', label: 'Yawn', moods: ['none'], ms: 2400, keys: [
    k(0, { p: 'neutral' }),
    k(350, { p: 'sleepy' }),
    k(800, { p: 'yawn', w: { y: -0.04, sy: 1.08, sx: 0.95 }, e: 'out', blend: 350 }),
    k(1500, { w: { y: -0.05, sy: 1.1, sx: 0.94 } }),
    k(1800, { p: 'sleepy', w: {}, e: 'io' }),
    k(2100, { w: { r: 4 }, e: 'io' }),
    k(2400, { w: { r: 3 }, p: 'sleepy', e: 'io' }),
  ] }),
  'peek-late': act({ name: 'peek-late', label: 'Peek in late', moods: ['none'], ms: 2400, keys: [
    k(0, { w: { x: 1.25, o: 0, r: -22 }, p: 'surprised' }),
    k(150, { w: { x: 1, r: -22 }, e: 'out' }),
    k(500, { w: { x: 0.6, r: -18 }, e: 'out' }),
    k(900, { w: { x: 0.6, r: -14 }, p: 'nervous' }),
    k(1150, { w: { x: 0.35, y: -0.08, r: -6 }, e: 'out' }),
    k(1300, { w: { x: 0.25 }, e: 'in' }),
    k(1450, { w: { x: 0.12, y: -0.07 }, e: 'out' }),
    k(1600, { w: { sy: 0.92, sx: 1.05 }, e: 'in' }),
    k(1800, { w: {}, p: 'shy', e: 'out' }),
    k(2400, { p: 'shy' }),
  ] }),

  // podium
  'crown-fly-in': act({ name: 'crown-fly-in', label: 'Fly in, crowned', moods: ['podium'], ms: 2600, burstAt: [0.5, 0.55], keys: [
    k(0, { w: { x: -0.7, y: -3, o: 0, r: -25 }, p: 'joy', burst: 0 }),
    k(100, { w: { x: -0.65, y: -2.8, r: -25 }, e: 'lin' }),
    ...flap(100, 1400),
    k(600, { w: { x: 0.45, y: -1.7, r: 15 }, e: 'io' }),
    k(1050, { w: { x: -0.25, y: -0.75, r: -10 }, e: 'io' }),
    k(1400, { w: { y: -0.15 }, e: 'io' }),
    k(1520, { w: { sy: 0.84, sx: 1.14 }, a: [40, -40], e: 'in', burst: 0 }),
    k(1700, { w: { y: -0.06, sy: 1.05 }, e: 'out' }),
    k(1880, { w: {}, e: 'in' }),
    k(2100, { burst: 1 }),
    k(2600, { p: 'proud' }),
  ] }),
  'victory-dance': act({ name: 'victory-dance', label: 'Victory dance', moods: ['podium'], ms: 2800, keys: [
    k(0, { p: 'joy' }),
    ...[0, 1, 2, 3, 4, 5, 6].flatMap((i) => {
      const t = 200 + i * 280; const left = i % 2 === 0;
      return [
        k(t, { w: { x: left ? -0.06 : 0.06, r: left ? -7 : 7, y: -0.06 }, a: left ? [145, -30] : [30, -145], e: 'out' }),
        k(t + 140, { w: { sy: 0.95, sx: 1.03 }, e: 'in' }),
      ];
    }),
    k(2500, { w: {}, p: 'proud', e: 'io' }),
    k(2800, { p: 'proud' }),
  ] }),
  'wave-crowd': act({ name: 'wave-crowd', label: 'Wave to the crowd', moods: ['podium'], ms: 2400, keys: [
    k(0, { p: 'happy' }),
    k(250, { a: [18, -140], w: { r: 4 }, e: 'out' }),
    k(400, { a: [18, -100] }), k(560, { a: [18, -140] }), k(720, { a: [18, -100] }), k(880, { a: [18, -140] }),
    k(1050, { a: [140, -30], w: { r: -4 }, e: 'io' }),
    k(1200, { a: [100, -30] }), k(1360, { a: [140, -30] }), k(1520, { a: [100, -30] }), k(1680, { a: [140, -30] }),
    k(2000, { w: {}, p: 'proud', e: 'io' }),
    k(2400, { p: 'proud' }),
  ] }),
  champion: act({ name: 'champion', label: 'Champion', moods: ['podium'], ms: 2200, burstAt: [0.5, 0.25], keys: [
    k(0, { p: 'proud', burst: 0 }),
    k(250, { w: { sy: 0.86, sx: 1.1 }, a: [30, -30], e: 'out', p: 'determined' }),
    k(500, { w: { y: -0.16, sy: 1.1, sx: 0.94 }, a: [140, -140], e: 'out', p: 'joy', burst: 0 }),
    k(700, { w: { y: -0.12 } }),
    k(900, { w: { sy: 0.94, sx: 1.04 }, e: 'in' }),
    k(1000, { burst: 1 }),
    k(1100, { w: {}, a: [115, -115], p: 'determined' }),
    k(1250, { a: [125, -125], w: { sy: 1.03 } }), k(1400, { a: [110, -110], w: {} }), k(1550, { a: [125, -125], w: { sy: 1.03 } }),
    k(1800, { w: {}, p: 'proud', e: 'io' }),
    k(2200, { p: 'proud' }),
  ] }),

  // idle: the lobby's parade
  'drop-in': act({ name: 'drop-in', label: 'Drop in', moods: ['idle'], ms: 1600, keys: dropKeys('neutral') }),
  wave: act({ name: 'wave', label: 'Wave', moods: ['idle'], ms: 1800, keys: [
    k(0, { p: 'neutral' }),
    k(200, { p: 'happy', a: [18, -140], w: { r: 3 }, e: 'out' }),
    k(400, { a: [18, -100] }), k(560, { a: [18, -140] }), k(720, { a: [18, -100] }), k(880, { a: [18, -140] }),
    k(1200, { a: [18, -18], w: {}, e: 'io' }),
    k(1800, { p: 'neutral' }),
  ] }),
  'little-dance': act({ name: 'little-dance', label: 'Little dance', moods: ['idle'], ms: 1800, keys: [
    k(0, { p: 'happy' }), ...steps(120, 4, 300, 0.7), k(1450, { w: {}, a: [18, -18], e: 'io' }), k(1800, { p: 'neutral' }),
  ] }),
  'five-right': act({ name: 'five-right', label: 'High five, right', moods: ['idle'], ms: 1800, burstAt: [1.04, 0.08], keys: [
    k(0, { p: 'neutral', burst: 0 }),
    k(250, { w: { sy: 0.92, sx: 1.05 }, a: [18, 0], e: 'out' }),
    k(550, { w: { x: 0.22, y: -0.08, r: 12 }, a: [18, -140], e: 'out', p: 'joy', burst: 0 }),
    k(700, { w: { x: 0.14, r: 6 }, e: 'out' }),
    k(1000, { w: {}, a: [18, -60], e: 'io', p: 'happy', burst: 1 }),
    k(1800, { p: 'neutral' }),
  ] }),
  'five-left': act({ name: 'five-left', label: 'High five, left', moods: ['idle'], ms: 1800, keys: [
    k(0, { p: 'neutral' }),
    k(250, { w: { sy: 0.92, sx: 1.05 }, a: [0, -18], e: 'out' }),
    k(550, { w: { x: -0.22, y: -0.08, r: -12 }, a: [140, -18], e: 'out', p: 'joy' }),
    k(700, { w: { x: -0.14, r: -6 }, e: 'out' }),
    k(1000, { w: {}, a: [60, -18], e: 'io', p: 'happy' }),
    k(1800, { p: 'neutral' }),
  ] }),
  peek: act({ name: 'peek', label: 'Peek', moods: ['idle'], ms: 2000, keys: [
    k(0, { p: 'neutral' }),
    k(350, { w: { x: -0.12, r: -18 }, e: 'out', p: 'skeptical' }),
    k(1000, { w: { x: -0.12, r: -18 } }),
    k(1150, { w: { x: 0.1, r: 16 }, e: 'io', p: 'surprised' }),
    k(1500, { w: { x: 0.1, r: 16 } }),
    k(1700, { w: {}, e: 'io', p: 'happy' }),
    k(2000, { p: 'neutral' }),
  ] }),
  wink: act({ name: 'wink', label: 'Wink', moods: ['idle'], ms: 1500, keys: [
    k(0, { p: 'neutral' }),
    k(250, { p: 'wink', w: { r: 5 }, e: 'out' }),
    k(500, { a: [18, -110] }), k(650, { a: [18, -132] }),
    k(1000, { w: {}, p: 'happy', e: 'io' }),
    k(1500, { p: 'neutral' }),
  ] }),
} as const satisfies Record<string, Act>;

export type ActName = keyof typeof ACTS;
export const ACT_NAMES = Object.keys(ACTS) as ActName[];
export const isActName = (s: string | null | undefined): s is ActName => !!s && Object.hasOwn(ACTS, s);

/** The acts picked at random for each mood (the lobby's drop-in and high fives are played by the parade). */
export const ACTS_FOR: Readonly<Record<CastMood, readonly ActName[]>> = {
  right: ['dance', 'high-five', 'fly', 'wink-point', 'jump-spin', 'backflip', 'drop-bounce'],
  wrong: ['splat', 'faint', 'cry', 'facepalm', 'deflate', 'shiver'],
  partial: ['shrug', 'think', 'wobble'],
  none: ['fall-asleep', 'yawn', 'peek-late'],
  podium: ['crown-fly-in', 'victory-dance', 'wave-crowd', 'champion'],
  idle: ['wave', 'little-dance', 'peek', 'wink'],
};

/** The pose an act ends on: its last key's. */
export const endPose = (a: Act): PoseName => [...a.keys].reverse().find((key) => key.p)!.p!;

// ---------- sampling ----------

const EASE: Record<Ease, (k: number) => number> = {
  lin: (k) => k,
  in: (k) => k * k,
  out: (k) => 1 - (1 - k) * (1 - k),
  io: (k) => (1 - Math.cos(Math.PI * k)) / 2,
  back: (k) => { const c = 1.70158; const m = k - 1; return 1 + (c + 1) * m * m * m + c * m * m; },
};
export const ease = (e: Ease | undefined, k: number) => EASE[e ?? 'io'](Math.min(1, Math.max(0, k)));
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;

interface Pt<T> { t: number; e?: Ease; v: T }
interface FacePt { t: number; blend: number; face: Face }
interface Compiled { w: Pt<WT>[]; arms: Pt<Arms>[]; face: FacePt[]; front: Pt<readonly [number, number]>[]; hand: Pt<number>[]; burst: Pt<number>[] }

const compiled = new WeakMap<Act, Compiled>();
function compile(a: Act): Compiled {
  let c = compiled.get(a);
  if (c) return c;
  c = { w: [], arms: [], face: [], front: [], hand: [], burst: [] };
  for (const key of a.keys) {
    if (key.w) c.w.push({ t: key.t, e: key.e, v: { ...REST, ...key.w } });
    if (key.a || key.p) c.arms.push({ t: key.t, e: key.e, v: key.a ?? POSES[key.p!].arms });
    if (key.p) c.face.push({ t: key.t, blend: key.blend ?? 220, face: faceOf(key.p) });
    if (key.front) c.front.push({ t: key.t, v: key.front });
    if (key.hand !== undefined) c.hand.push({ t: key.t, e: key.e, v: key.hand });
    if (key.burst !== undefined) c.burst.push({ t: key.t, e: key.e, v: key.burst });
  }
  compiled.set(a, c);
  return c;
}

function along<T>(pts: Pt<T>[], t: number, mix: (a: T, b: T, k: number) => T, none: T): T {
  if (!pts.length) return none;
  if (t <= pts[0].t) return pts[0].v;
  for (let i = 1; i < pts.length; i++) {
    const b = pts[i];
    if (t < b.t) { const a = pts[i - 1]; return mix(a.v, b.v, ease(b.e, (t - a.t) / (b.t - a.t))); }
  }
  return pts[pts.length - 1].v;
}

const mixW = (a: WT, b: WT, k: number): WT => ({
  x: lerp(a.x, b.x, k), y: lerp(a.y, b.y, k), r: lerp(a.r, b.r, k), s: lerp(a.s, b.s, k), sx: lerp(a.sx, b.sx, k), sy: lerp(a.sy, b.sy, k), o: lerp(a.o, b.o, k),
});
export const mixArms = (a: Arms, b: Arms, k: number): Arms => [lerp(a[0], b[0], k), lerp(a[1], b[1], k)];
const mixN = (a: number, b: number, k: number) => lerp(a, b, k);

function faceAt(pts: FacePt[], t: number): Face {
  if (t <= pts[0].t) return pts[0].face;
  for (let i = 1; i < pts.length; i++) {
    const b = pts[i];
    if (t < b.t) {
      const a = pts[i - 1];
      const span = Math.min(b.blend, b.t - a.t);
      const from = b.t - span;
      return t < from ? a.face : mixFace(a.face, b.face, ease('out', (t - from) / span));
    }
  }
  return pts[pts.length - 1].face;
}

export interface ActFrame { w: WT; arms: Arms; face: Face; front: readonly [number, number]; hand: number; burst: number }

/** Where an act is at t ms from its start (clamped to the act). */
export function sampleAct(a: Act, t: number): ActFrame {
  const c = compile(a);
  const at = Math.min(a.ms, Math.max(0, t));
  let front: readonly [number, number] = [0, 0];
  for (const p of c.front) if (p.t <= at) front = p.v;
  return {
    w: along(c.w, at, mixW, REST),
    arms: along(c.arms, at, mixArms, POSES.neutral.arms),
    face: faceAt(c.face, at),
    front,
    hand: along(c.hand, at, mixN, 0),
    burst: along(c.burst, at, mixN, 0),
  };
}

// ---------- after the act: the mood's gentle loop ----------

const TAU = Math.PI * 2;
const wave = (ms: number, period: number) => Math.sin((ms / period) * TAU);
/** 0 at rest, rising to 1 and back each period (starts at rest). */
const swell = (ms: number, period: number) => (1 - Math.cos((ms / period) * TAU)) / 2;

/** The loop each mood keeps after its act: joy bounces, a miss sighs, sleep sways. Offsets on the act's last state. */
export function moodLoop(mood: CastMood, ms: number): Partial<WT> {
  const fade = Math.min(1, ms / 600);
  switch (mood) {
    case 'right': {
      // little hops, a squash between them
      const p = (ms % 1100) / 1100;
      const hop = p < 0.45 ? Math.sin((p / 0.45) * Math.PI) : 0;
      const land = p >= 0.45 && p < 0.6 ? Math.sin(((p - 0.45) / 0.15) * Math.PI) : 0;
      return { y: -0.07 * hop * fade, sy: 1 + (0.03 * hop - 0.05 * land) * fade, sx: 1 + (0.03 * land - 0.015 * hop) * fade };
    }
    case 'wrong': {
      const s = swell(ms, 3600);
      return { y: 0.02 * s * fade, sy: 1 - 0.035 * s * fade, sx: 1 + 0.015 * s * fade, r: -2 * s * fade };
    }
    case 'partial': return { r: 3.5 * wave(ms, 2600) * fade, x: 0.015 * wave(ms, 2600) * fade };
    case 'none': return { r: 4 * wave(ms, 3200) * fade, y: 0.01 * swell(ms, 1600) * fade };
    case 'podium': return { y: -0.025 * swell(ms, 1600) * fade, r: 2 * wave(ms, 3200) * fade, sy: 1 + 0.02 * swell(ms, 1600) * fade };
    default: return { y: -0.025 * swell(ms, 2600) * fade, r: 1.5 * wave(ms, 5200) * fade };
  }
}

/** A loop's offsets on a base state. */
export const onTop = (base: WT, d: Partial<WT>): WT => ({
  x: base.x + (d.x ?? 0), y: base.y + (d.y ?? 0), r: base.r + (d.r ?? 0),
  s: base.s, sx: base.sx * (d.sx ?? 1), sy: base.sy * (d.sy ?? 1), o: base.o,
});

// ---------- picking ----------

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
/** A number in [0, 1) from a string, the same every time. */
export function unit(s: string): number {
  let t = (hash(s) + 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/**
 * The act for a slot at question `index`: random from the seed (battle code, plus the player on a phone), and never the
 * act the same slot played at index − 1 in the same mood. The same seed and index give the same act on every reload.
 */
export function pickAct(mood: CastMood, seed: string, index: number): ActName | null {
  const list = ACTS_FOR[mood];
  if (!list.length) return null;
  if (list.length === 1) return list[0];
  let prev = -1;
  for (let i = 0; i <= Math.max(0, Math.floor(index)); i++) {
    const n = prev < 0 ? list.length : list.length - 1;
    let pick = Math.floor(unit(`${seed}|${mood}|${i}`) * n);
    if (prev >= 0 && pick >= prev) pick++;
    prev = pick;
  }
  return list[prev];
}

/** Any act for a mood but `prev` (the gallery's random button). */
export function randomAct(mood: CastMood, prev?: string | null, rand: () => number = Math.random): ActName | null {
  const list = ACTS_FOR[mood].filter((a) => a !== prev);
  return list.length ? list[Math.floor(rand() * list.length)] : prev && isActName(prev) ? prev : null;
}

/** What a lone character plays when nobody chooses: the mood's first pick for its name; idle plays nothing. */
export const defaultAct = (mood: CastMood, name: string): ActName | null => (mood === 'idle' ? null : pickAct(mood, name, 0));

// ---------- the lobby's parade ----------

export type ParadeMove = 'wave' | 'little-dance' | 'peek' | 'wink' | 'five';
export const PARADE_MOVES: readonly ParadeMove[] = ['wave', 'little-dance', 'peek', 'wink', 'five'];
export interface ParadeTurn { move: ParadeMove; who: number[]; acts: ActName[] }

/**
 * The parade's next turn: one character (or two neighbours for a high five, when there are three or more), never the
 * same move twice in a row and never the same character leading twice in a row. Never everyone at once.
 */
export function paradeTurn(count: number, last: ParadeTurn | undefined, rand: () => number = Math.random): ParadeTurn | undefined {
  if (count < 1) return undefined;
  const moves = PARADE_MOVES.filter((m) => m !== last?.move && (m !== 'five' || count >= 3));
  const move = moves[Math.floor(rand() * moves.length)] ?? 'wave';
  const lead = last?.who[0];
  if (move === 'five') {
    const firsts = Array.from({ length: count - 1 }, (_, i) => i).filter((i) => i !== lead);
    const i = firsts.length ? firsts[Math.floor(rand() * firsts.length)] : 0;
    return { move, who: [i, i + 1], acts: ['five-right', 'five-left'] };
  }
  const people = Array.from({ length: count }, (_, i) => i).filter((i) => count === 1 || i !== lead);
  const i = people[Math.floor(rand() * people.length)];
  return { move, who: [i], acts: [move] };
}
